import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { getApiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { BrandLogo } from "@/components/BrandLogo";
import { Loader2, GraduationCap } from "lucide-react";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register, loginWithGoogle } = useAuth();
  // Read `next` from either the react-router state (Header/RequireAuth pass it)
  // or the ?next= query param (Paywall/anon sim-gate). Falls back to /sections.
  const nextFromQuery = new URLSearchParams(location.search).get("next");
  const from = location.state?.from || nextFromQuery || "/sections";

  const [tab, setTab] = useState("login"); // login | register
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (tab === "register") {
        await register(name, email, password);
      } else {
        await login(email, password);
      }
      navigate(from, { replace: true });
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F7F9] flex flex-col">
      <header className="border-b border-slate-200 bg-white/70 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <Link to="/sections" className="flex items-center gap-3 w-fit" data-testid="brand-logo-home">
            <BrandLogo chip />
            <span className="hidden sm:block text-[11px] text-slate-500 border-l border-slate-200 pl-3">
              Veterinary clinical simulation training
            </span>
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12 fade-up">
        <Card className="w-full max-w-md border-slate-200 p-8">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#0047AB] font-semibold mb-2">
            <GraduationCap className="w-3.5 h-3.5" />
            <span>Trainee access</span>
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            {tab === "login" ? "Sign in to continue" : "Create your account"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Save and track your simulator scores and grades.
          </p>

          <div className="mt-5 grid grid-cols-2 gap-1 bg-slate-100 rounded-md p-1">
            {[
              { id: "login", label: "Sign in" },
              { id: "register", label: "Register" },
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => {
                  setTab(t.id);
                  setError("");
                }}
                data-testid={`auth-tab-${t.id}`}
                className={`text-sm font-semibold py-2 rounded transition-colors ${
                  tab === t.id ? "bg-white text-[#0047AB] shadow-sm" : "text-slate-500"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="mt-5 space-y-4">
            {tab === "register" && (
              <div className="space-y-1.5">
                <Label htmlFor="name">Full name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="auth-name-input"
                  placeholder="Jane Nurse"
                  required
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="auth-email-input"
                placeholder="you@clinic.com"
                required
              />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                {tab === "login" && (
                  <Link to="/forgot-password" data-testid="forgot-password-link"
                    className="text-xs font-semibold text-[#0047AB] hover:underline">
                    Forgot password?
                  </Link>
                )}
              </div>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="auth-password-input"
                placeholder="••••••••"
                minLength={6}
                required
              />
            </div>

            {error && (
              <div
                className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2"
                data-testid="auth-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={busy}
              data-testid="auth-submit-btn"
              className="w-full bg-[#0047AB] hover:bg-[#003380] text-white"
            >
              {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
              {tab === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>

          <div className="mt-5 flex items-center gap-3">
            <div className="h-px bg-slate-200 flex-1" />
            <span className="text-[11px] uppercase tracking-wider text-slate-400">or</span>
            <div className="h-px bg-slate-200 flex-1" />
          </div>

          <button
            type="button"
            onClick={loginWithGoogle}
            data-testid="google-signin-btn"
            className="mt-4 w-full flex items-center justify-center gap-2.5 border border-slate-300 rounded-md py-2.5 text-sm font-semibold text-slate-700 bg-white hover:bg-slate-50 transition-colors"
          >
            <svg className="w-4 h-4" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35 24 35c-6.1 0-11-4.9-11-11s4.9-11 11-11c2.8 0 5.4 1.1 7.3 2.8l5.7-5.7C33.6 6.1 29 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z" />
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c2.8 0 5.4 1.1 7.3 2.8l5.7-5.7C33.6 6.1 29 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
              <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35 26.7 36 24 36c-5.3 0-9.7-2.6-11.3-7l-6.5 5C9.6 39.6 16.2 44 24 44z" />
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.6l6.2 5.2C39.9 36.7 44 31 44 24c0-1.3-.1-2.3-.4-3.5z" />
            </svg>
            Continue with Google
          </button>

          <Link
            to="/sections"
            data-testid="login-home-link"
            className="block text-center text-xs text-slate-400 hover:text-slate-600 mt-5"
          >
            ← Back to Simulator Hub
          </Link>
        </Card>
      </main>
    </div>
  );
}
